import {CalendarEventResource} from "../../../shared/types/calendar-types/calendarEventResource";
import {endTimeCalculation} from "./calendar-helper.mapper";

function fallbackMaskedString(value: string | null | undefined, text: string): string {
  return value ?? text;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function mapCalendarEventResourceToSyncfusionEvent(event: CalendarEventResource, calendarId: (number | string), translate: boolean, maskedText: string): any {
  return {
    Id: event.id,
    Subject: fallbackMaskedString((translate && event.name_translated !== null) ? event.name_translated : event.name, maskedText),
    Description: fallbackMaskedString((translate && event.description_translated !== null) ? event.description_translated : event.description, maskedText),
    Location: ((translate && event.location_translated !== null) ? event.location_translated : event.location) === null
      ? null
      : fallbackMaskedString((translate && event.location_translated !== null) ? event.location_translated : event.location, maskedText),
    Priority: event.priority,
    StartTime: event.timing.start_at,
    EndTime: endTimeCalculation(new Date(event.timing.start_at, ), event.timing.duration, event.timing.is_full_day).toISOString(),
    IsAllDay: event.timing.is_full_day,
    StartTimezone: event.timing.timezone,
    EndTimezone: event.timing.timezone,
    RecurrenceRule: event.recurrence.rrule,
    RecurrenceID: event.override.parent_id ?? null,
    RecurrenceException: mapRecurrenceExceptions(
      [...event.recurrence.deleted_occurrences, ...event.recurrence.overridden_occurrences],
      event.timing.start_at,
      event.timing.timezone
    ),
    CalendarId: calendarId,
    ParticipantsUserIDs: event.participants.user_ids,
    ParticipantsGroupIDs: event.participants.group_ids,
    ParticipantsMails: event.participants.external_user_emails,
    PushUserIDs: event.participants.notify_user_ids,
    PushGroupIDs: event.participants.notify_group_ids,
    IsReadonly: !event.permission.editable,
    maxParticipants: event.capacity,
    notifyBeforeMinutes: event.timing.notify_before,
    isEditable: true,

    //custom color field
    eventColor: event.priority === 
    'high' ?
    'var(--our-error-red)' :
    event.priority === "medium" ?
      'var(--our-yellow)' :
      event.priority === "low" ? 
      'var(--our-light-green)' :
      'var(--our-asphalt)'
      ,
    // calendar origin
    originCalendarId: event.calendar_id
  };
}

function mapRecurrenceExceptions(exceptions: string[], start: string, timezone: string): string {
  if (!exceptions || exceptions.length === 0) return '';

  const startTimeFormatter = new Intl.DateTimeFormat(undefined, {
    timeZone: timezone,
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23'
  });
  const startParts = startTimeFormatter.formatToParts(new Date(start));
  const hour = startParts.find(part => part.type === 'hour')?.value;
  const minute = startParts.find(part => part.type === 'minute')?.value;
  const second = startParts.find(part => part.type === 'second')?.value;
  if (!hour || !minute || !second) return '';

  return exceptions.map(ex => {
    const [year, month, day] = ex.split('-').map(Number);
    if (!year || !month || !day) return '';

    const exceptionUtc = convertZonedPartsToUtc(
      year,
      month,
      day,
      Number(hour),
      Number(minute),
      Number(second),
      timezone
    );

    return formatUtcRecurrenceException(exceptionUtc);
  }).join(',');
}

function convertZonedPartsToUtc(
  year: number,
  month: number,
  day: number,
  hour: number,
  minute: number,
  second: number,
  timezone: string
): Date {
  const utcGuess = new Date(Date.UTC(year, month - 1, day, hour, minute, second));
  const offsetMinutes = getTimezoneOffsetMinutes(utcGuess, timezone);
  const resolvedUtc = new Date(utcGuess.getTime() - offsetMinutes * 60_000);
  const resolvedKey = getZonedDateTimeKey(resolvedUtc, timezone);
  const targetKey = `${year}-${pad2(month)}-${pad2(day)}T${pad2(hour)}:${pad2(minute)}:${pad2(second)}`;

  if (resolvedKey === targetKey) {
    return resolvedUtc;
  }

  const resolvedAsUtc = Date.UTC(
    Number(resolvedKey.slice(0, 4)),
    Number(resolvedKey.slice(5, 7)) - 1,
    Number(resolvedKey.slice(8, 10)),
    Number(resolvedKey.slice(11, 13)),
    Number(resolvedKey.slice(14, 16)),
    Number(resolvedKey.slice(17, 19))
  );
  const targetAsUtc = Date.UTC(year, month - 1, day, hour, minute, second);

  return new Date(resolvedUtc.getTime() + (targetAsUtc - resolvedAsUtc));
}

function getTimezoneOffsetMinutes(date: Date, timezone: string): number {
  const offsetFormatter = new Intl.DateTimeFormat(undefined, {
    timeZone: timezone,
    timeZoneName: 'longOffset'
  });
  const offsetValue = offsetFormatter.formatToParts(date).find(part => part.type === 'timeZoneName')?.value;
  if (!offsetValue || offsetValue === 'GMT') return 0;

  const match = offsetValue.match(/^GMT([+-])(\d{2}):(\d{2})$/);
  if (!match) return 0;

  const [, sign, hours, minutes] = match;
  const totalMinutes = Number(hours) * 60 + Number(minutes);
  return sign === '+' ? totalMinutes : -totalMinutes;
}

function getZonedDateTimeKey(date: Date, timezone: string): string {
  const parts = new Intl.DateTimeFormat(undefined, {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23'
  }).formatToParts(date);

  const year = parts.find(part => part.type === 'year')?.value;
  const month = parts.find(part => part.type === 'month')?.value;
  const day = parts.find(part => part.type === 'day')?.value;
  const hour = parts.find(part => part.type === 'hour')?.value;
  const minute = parts.find(part => part.type === 'minute')?.value;
  const second = parts.find(part => part.type === 'second')?.value;

  return `${year}-${month}-${day}T${hour}:${minute}:${second}`;
}

function formatUtcRecurrenceException(date: Date): string {
  return `${date.getUTCFullYear()}${pad2(date.getUTCMonth() + 1)}${pad2(date.getUTCDate())}T${pad2(date.getUTCHours())}${pad2(date.getUTCMinutes())}${pad2(date.getUTCSeconds())}Z`;
}

function pad2(value: number): string {
  return value.toString().padStart(2, '0');
}
